import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useTheme } from "../context/ThemeContext";
import { getSecondaryText } from "../utils/themeUtils";
import { PageContainer, PageHeading, Card, Button, SectionHeading } from "../components/ui";
import {
  getTrialStatus,
  convertTrialToPaid,
  TrialStatus,
  formatTrialExpiration,
  calculateDaysRemaining,
} from "../api/trialAPI";
import { Loader2, CheckCircle, AlertTriangle, ArrowRight } from "lucide-react";

const TrialConvert: React.FC = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();

  // Get tenant and token
  const pathParts = window.location.pathname.split("/").filter(Boolean);
  const tenantSlug = pathParts[0] || "";
  const token = localStorage.getItem("authToken") || "";

  // State
  const [loading, setLoading] = useState(true);
  const [converting, setConverting] = useState(false);
  const [trialStatus, setTrialStatus] = useState<TrialStatus | null>(null);
  const [conversionComplete, setConversionComplete] = useState(false);

  // Fetch trial status on mount
  useEffect(() => {
    const fetchStatus = async () => {
      if (!token) {
        navigate(`/${tenantSlug}/login`);
        return;
      }

      setLoading(true);
      const response = await getTrialStatus(token);
      if (response.success) {
        setTrialStatus(response.data);
      }
      setLoading(false);
    };
    fetchStatus();
  }, [token, tenantSlug, navigate]);

  const handleConvert = async () => {
    setConverting(true);

    const response = await convertTrialToPaid(token);

    if (response.success) {
      setConversionComplete(true);
      toast.success("Welcome to full membership!");
    } else {
      toast.error(response.message || "Failed to convert membership");
      setConverting(false);
    }
  };

  const handleContinueToEnrollment = () => {
    // Navigate to athlete enrollment
    navigate(`/${tenantSlug}/member/athlete/add`);
  };

  // Loading state
  if (loading) {
    return (
      <PageContainer width="form">
        <div className="min-h-[400px] flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      </PageContainer>
    );
  }

  // Not a trial member
  if (!trialStatus?.is_trial) {
    return (
      <PageContainer width="form">
        <PageHeading
          title="Already a Full Member"
          subtitle="Your account already has full membership access."
        />
        <Card>
          <div className="flex items-center gap-3">
            <CheckCircle className="w-8 h-8 text-green-500 flex-shrink-0" />
            <Button variant="primary" onClick={() => navigate(`/${tenantSlug}/dashboard`)}>
              Go to Dashboard
            </Button>
          </div>
        </Card>
      </PageContainer>
    );
  }

  // Conversion complete - prompt for enrollment
  if (conversionComplete) {
    return (
      <PageContainer width="form">
        <PageHeading
          title="Membership Activated!"
          subtitle="Your membership has been successfully upgraded. Now let's get your athletes enrolled in programs!"
        />
        <Card>
          <div className="flex items-center gap-3 mb-6">
            <CheckCircle className="w-10 h-10 text-green-500 flex-shrink-0" />
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              variant="primary"
              onClick={handleContinueToEnrollment}
              trailingIcon={<ArrowRight className="w-5 h-5" />}
            >
              Continue to Enrollment
            </Button>
            <Button
              variant="secondary"
              onClick={() => navigate(`/${tenantSlug}/dashboard`)}
            >
              Skip for now
            </Button>
          </div>
        </Card>
      </PageContainer>
    );
  }

  // Trial member - show conversion UI
  const daysRemaining = calculateDaysRemaining(trialStatus.trial_expires_at);
  const isExpired = trialStatus.is_expired;

  return (
    <PageContainer width="form">
      <PageHeading
        title={isExpired ? "Your Trial Has Expired" : "Your Trial Is Ending Soon"}
        subtitle={
          isExpired
            ? "Upgrade now to continue enjoying all the benefits of membership."
            : `You have ${daysRemaining} day${daysRemaining !== 1 ? "s" : ""} remaining in your trial.`
        }
      />
      <Card>
        {/* Countdown */}
        <div className="flex items-center gap-4 mb-6">
          {isExpired ? (
            <AlertTriangle className="w-12 h-12 text-yellow-500 flex-shrink-0" />
          ) : (
            <div
              className={`w-12 h-12 rounded-full ${isDarkMode ? "bg-blue-900" : "bg-blue-100"} flex items-center justify-center flex-shrink-0`}
            >
              <span className={`text-xl font-bold ${isDarkMode ? "text-blue-300" : "text-blue-600"}`}>
                {daysRemaining}
              </span>
            </div>
          )}
          {trialStatus.trial_expires_at && (
            <p className={`${getSecondaryText(isDarkMode)} text-sm`}>
              {isExpired ? "Expired on" : "Trial ends"}: {formatTrialExpiration(trialStatus.trial_expires_at)}
            </p>
          )}
        </div>

        {/* Benefits */}
        <div className={`mb-6 p-4 rounded-lg ${isDarkMode ? "bg-gray-700" : "bg-gray-50"}`}>
          <SectionHeading as="h3">With full membership, you can:</SectionHeading>
          <ul className={`space-y-2 ${getSecondaryText(isDarkMode)}`}>
            <li className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
              Enroll athletes in programs and classes
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
              Access all member features and benefits
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
              Track progress and manage your family account
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
              Access exclusive member events and camps
            </li>
          </ul>
        </div>

        {/* Action */}
        <Button
          variant="primary"
          fullWidth
          onClick={handleConvert}
          disabled={converting}
          icon={converting ? <Loader2 className="w-5 h-5 animate-spin" /> : undefined}
        >
          {converting ? "Activating Membership..." : "Become a Full Member"}
        </Button>

        <p className={`text-sm ${getSecondaryText(isDarkMode)} mt-4`}>
          You'll be able to enroll your athletes and set up billing after activation.
        </p>
      </Card>
    </PageContainer>
  );
};

export default TrialConvert;
